import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Link,
  MenuItem,
  Stack,
  TextField,
} from "@mui/material";
import dayjs from "dayjs";
import { useIntl } from "react-intl";
import { useSnackbar } from "notistack";
import RevaText from "components/common/RevaText";
import { formatFinancialAmount } from "../financial/financial-helpers";
import {
  DESCRIPTION_MAX_LENGTH,
  amountErrorId,
  descriptionErrorId,
  descriptionHelperText,
  isValidAmount,
  moneyFieldError,
  moneyRequestErrorMessage,
  overpaymentAmount,
} from "../financial/money-validation";
import PaymentProofDropzone from "./PaymentProofDropzone";

const invoiceDescription = (invoice) =>
  invoice.description || invoice.invoice_items?.[0]?.description || `#${invoice.id}`;

const PlayerPaymentWizard = ({
  player,
  selectedInvoices,
  availableBalance = null,
  currency,
  hasCollectionProcessor,
  onRegisterManual,
  onGenerateLink,
  onVoidLink,
  onRefresh,
  onSuccess,
  onBack,
}) => {
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const [initialSelection] = useState(() => ({
    invoices: selectedInvoices,
    custom: selectedInvoices.length === 0,
  }));
  const custom = initialSelection.custom;
  const wizardInvoices = initialSelection.invoices;
  const [mode, setMode] = useState(custom ? "manual" : null);
  const [method, setMethod] = useState("cash");
  const [proof, setProof] = useState(null);
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [remaining, setRemaining] = useState(0);
  const invoiceIds = wizardInvoices.map((invoice) => invoice.id);
  const total = wizardInvoices.reduce((sum, invoice) => sum + Number(invoice.final_amount || 0), 0);

  useEffect(() => {
    if (!result?.expires_at || result?.voided || result?.expired) return undefined;
    let expired = false;
    const update = async () => {
      const seconds = Math.max(0, dayjs(result.expires_at).diff(dayjs(), "second"));
      setRemaining(seconds);
      if (seconds === 0 && !expired) {
        expired = true;
        setResult((previous) => ({ ...previous, expired: true }));
        setSaving(true);
        try {
          await onRefresh?.();
        } finally {
          setSaving(false);
        }
      }
    };
    update();
    const timer = window.setInterval(update, 1000);
    return () => window.clearInterval(timer);
  }, [onRefresh, result]);

  const customInvalid =
    custom && (!isValidAmount(amount) || !description.trim() || Boolean(descriptionErrorId(description)));
  const amountError = moneyFieldError(intl, amountErrorId(amount));
  const excess = custom && isValidAmount(amount) ? overpaymentAmount(amount, availableBalance) : 0;

  const submitManual = async () => {
    if (!proof || customInvalid) return;
    setSaving(true);
    setError(null);
    try {
      await onRegisterManual({
        custom,
        invoiceIds,
        amount: Number(amount),
        description: description.trim(),
        method,
        proof,
      });
      setResult({ kind: "manual" });
      onSuccess?.();
    } catch (requestError) {
      setError(moneyRequestErrorMessage(requestError, intl));
    } finally {
      setSaving(false);
    }
  };

  const generateLink = async () => {
    setSaving(true);
    setError(null);
    try {
      const payload = await onGenerateLink(invoiceIds);
      setResult({ kind: "link", ...payload, voided: false });
      onRefresh?.();
    } catch (requestError) {
      setError(moneyRequestErrorMessage(requestError, intl));
    } finally {
      setSaving(false);
    }
  };

  const voidLink = async () => {
    setSaving(true);
    setError(null);
    try {
      await onVoidLink(result.payment_id);
      setResult((previous) => ({ ...previous, voided: true }));
      onRefresh?.();
    } catch (requestError) {
      setError(moneyRequestErrorMessage(requestError, intl));
    } finally {
      setSaving(false);
    }
  };

  const copyLink = async () => {
    if (!result?.payment_url || !navigator.clipboard?.writeText) return;
    try {
      await navigator.clipboard.writeText(result.payment_url);
      enqueueSnackbar(intl.formatMessage({ id: "gestion.playerFinance.linkCopied" }), { variant: "success" });
    } catch {
      enqueueSnackbar(intl.formatMessage({ id: "gestion.playerFinance.linkCopyFailed" }), { variant: "error" });
    }
  };

  if (result?.kind === "manual") {
    return (
      <Stack spacing={2}>
        <RevaText variant="h6">{intl.formatMessage({ id: "gestion.playerFinance.paymentRecorded" })}</RevaText>
        {!custom &&
          wizardInvoices.map((invoice) => (
            <RevaText key={invoice.id}>
              {invoiceDescription(invoice)} — {formatFinancialAmount(invoice.final_amount, currency)}
            </RevaText>
          ))}
        {custom && <RevaText>{description}</RevaText>}
        <RevaText sx={{ fontWeight: 600 }}>
          {intl.formatMessage({ id: "gestion.playerFinance.total" })}:{" "}
          {formatFinancialAmount(custom ? amount : total, currency)}
        </RevaText>
        <Button variant="contained" onClick={onBack}>
          {intl.formatMessage({ id: "gestion.playerFinance.returnAccount" })}
        </Button>
      </Stack>
    );
  }

  if (result?.kind === "link") {
    const minutes = String(Math.floor(remaining / 60)).padStart(2, "0");
    const seconds = String(remaining % 60).padStart(2, "0");
    const phone = String(player?.phone_number || "").replace(/\D/g, "");
    const whatsappUrl =
      phone && result.payment_url
        ? `https://wa.me/${phone}?text=${encodeURIComponent(result.payment_url)}`
        : null;

    return (
      <Stack spacing={2}>
        <RevaText variant="h6">
          {intl.formatMessage({
            id: result.expired
              ? "gestion.playerFinance.linkExpired"
              : result.voided
                ? "gestion.playerFinance.linkVoided"
                : "gestion.playerFinance.linkPending",
          })}
        </RevaText>
        <RevaText sx={{ fontWeight: 600 }}>
          {intl.formatMessage({ id: "gestion.playerFinance.total" })}:{" "}
          {formatFinancialAmount(result.amount, result.currency || currency)}
        </RevaText>
        <Link href={result.payment_url} target="_blank" rel="noopener noreferrer" sx={{ wordBreak: "break-all" }}>
          {result.payment_url}
        </Link>
        {!result.voided && !result.expired && (
          <RevaText>{intl.formatMessage({ id: "gestion.playerFinance.countdown" }, { time: `${minutes}:${seconds}` })}</RevaText>
        )}
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
          <Button
            variant="outlined"
            onClick={copyLink}
          >
            {intl.formatMessage({ id: "gestion.playerFinance.copyLink" })}
          </Button>
          {whatsappUrl && (
            <Button component="a" href={whatsappUrl} target="_blank" rel="noopener noreferrer" variant="outlined">
              WhatsApp
            </Button>
          )}
          {!result.voided && !result.expired && (
            <Button color="error" variant="outlined" disabled={saving} onClick={voidLink}>
              {intl.formatMessage({ id: "gestion.playerFinance.voidLink" })}
            </Button>
          )}
          {(result.voided || result.expired) && (
            <Button variant="contained" disabled={saving} onClick={generateLink}>
              {intl.formatMessage({ id: "gestion.playerFinance.regenerateLink" })}
            </Button>
          )}
        </Stack>
        {error && <Alert severity="error">{error}</Alert>}
        <Button onClick={onBack}>{intl.formatMessage({ id: "gestion.playerFinance.returnAccount" })}</Button>
      </Stack>
    );
  }

  if (!custom && !mode) {
    return (
      <Stack spacing={2}>
        <RevaText variant="h6">{intl.formatMessage({ id: "gestion.playerFinance.paymentSummary" })}</RevaText>
        {wizardInvoices.map((invoice) => (
          <RevaText key={invoice.id}>
            {invoiceDescription(invoice)} — {formatFinancialAmount(invoice.final_amount, currency)}
          </RevaText>
        ))}
        <RevaText sx={{ fontWeight: 600 }}>
          {intl.formatMessage({ id: "gestion.playerFinance.total" })}: {formatFinancialAmount(total, currency)}
        </RevaText>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
          <Button variant="contained" onClick={() => setMode("manual")}>
            {intl.formatMessage({ id: "gestion.playerFinance.manualPayment" })}
          </Button>
          <Button variant="outlined" disabled={!hasCollectionProcessor || saving} onClick={generateLink}>
            {intl.formatMessage({ id: "gestion.playerFinance.generateLink" })}
          </Button>
        </Stack>
        {!hasCollectionProcessor && (
          <Alert severity="info">{intl.formatMessage({ id: "gestion.financials.linkNeedsProcessor" })}</Alert>
        )}
        {error && <Alert severity="error">{error}</Alert>}
        <Button onClick={onBack}>{intl.formatMessage({ id: "gestion.playerFinance.back" })}</Button>
      </Stack>
    );
  }

  return (
    <Stack spacing={2}>
      <RevaText variant="h6">
        {intl.formatMessage({
          id: custom ? "gestion.playerFinance.customPayment" : "gestion.playerFinance.manualPayment",
        })}
      </RevaText>
      {custom && (
        <>
          <TextField
            type="number"
            label={`${intl.formatMessage({ id: "gestion.playerFinance.amount" })}${currency ? ` (${currency})` : ""}`}
            value={amount}
            onChange={(event) => {
              setAmount(event.target.value);
              setError(null);
            }}
            error={Boolean(amountError)}
            helperText={amountError ?? undefined}
            inputProps={{ min: 0.01, step: 0.01 }}
          />
          <TextField
            required
            label={intl.formatMessage({ id: "gestion.playerFinance.description" })}
            value={description}
            onChange={(event) => {
              setDescription(event.target.value);
              setError(null);
            }}
            error={Boolean(descriptionErrorId(description))}
            helperText={descriptionHelperText(intl, description)}
            inputProps={{ maxLength: DESCRIPTION_MAX_LENGTH }}
          />
          {excess > 0 && (
            <Alert data-testid="overpayment-warning" severity="info">
              {intl.formatMessage(
                { id: "gestion.playerFinance.overpaymentCredit" },
                {
                  debt: formatFinancialAmount(Math.max(0, -Number(availableBalance)), currency),
                  excess: formatFinancialAmount(excess, currency),
                },
              )}
            </Alert>
          )}
        </>
      )}
      <TextField
        select
        label={intl.formatMessage({ id: "gestion.playerFinance.method" })}
        value={method}
        onChange={(event) => setMethod(event.target.value)}
      >
        <MenuItem value="cash">{intl.formatMessage({ id: "gestion.financials.methodCash" })}</MenuItem>
        <MenuItem value="transfer">{intl.formatMessage({ id: "gestion.financials.methodTransfer" })}</MenuItem>
        <MenuItem value="pos">{intl.formatMessage({ id: "gestion.financials.methodPos" })}</MenuItem>
      </TextField>
      <PaymentProofDropzone file={proof} onChange={setProof} disabled={saving} />
      {error && <Alert severity="error">{error}</Alert>}
      <Stack direction="row" spacing={1}>
        <Button
          variant="contained"
          disabled={saving || !proof || customInvalid}
          onClick={submitManual}
        >
          {intl.formatMessage({ id: "gestion.playerFinance.confirmPayment" })}
        </Button>
        <Button onClick={() => (custom ? onBack() : setMode(null))} disabled={saving}>
          {intl.formatMessage({ id: "gestion.playerFinance.back" })}
        </Button>
      </Stack>
    </Stack>
  );
};

export default PlayerPaymentWizard;
